import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { Eye } from "lucide-react";
import { AdminLogger } from "@/lib/adminLogger";

interface Claim {
  id: string;
  claim_number: string;
  loss_date: string;
  settlement_date: string | null;
  settlement_amount: number | null;
  claim_type: string;
  adjuster_name: string;
  policyholder_name: string;
  status: "pending" | "in_review" | "approved" | "denied";
  created_at: string;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (!session?.user) {
          navigate("/auth");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      checkUserRole();
      checkAdminStatus();
      fetchClaims();
    }
  }, [user]);

  // Real-time subscription for claim status updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('claims-status-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'claims'
        },
        (payload) => {
          // Claim updated via realtime
          
          // Update the claim in the list
          setClaims((prev) => 
            prev.map((claim) => 
              claim.id === payload.new.id 
                ? { ...claim, ...payload.new } 
                : claim
            )
          );
          
          // Show toast notification for status changes
          if (payload.old.status !== payload.new.status) {
            toast.info(`Claim ${payload.new.claim_number} status updated to ${payload.new.status.replace('_', ' ')}`);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user?.id)
        .eq("role", "admin")
        .maybeSingle();

      if (error) throw error;
      setIsAdmin(!!data);
    } catch (error: any) {
      console.error("Error checking admin status:", error);
    }
  };

  const checkUserRole = async () => {
    if (!user) return;

    try {
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .maybeSingle();

      if (roleData?.role) {
        switch (roleData.role) {
          case "landlord":
            navigate("/landlord-dashboard");
            break;
          case "adjuster":
            navigate("/adjuster-dashboard");
            break;
          case "client":
            navigate("/client-dashboard");
            break;
          case "admin":
            // Admins stay on the main dashboard
            break;
          default:
            // Regular users stay on the main dashboard
            break;
        }
      }
    } catch (error) {
      console.error("Error checking user role:", error);
    }
  };

  const fetchClaims = async () => {
    try {
      let query = supabase.from("claims").select("*");
      
      // Regular users only see their own claims
      // Admins see all claims (RLS policies handle this)
      if (!isAdmin) {
        query = query.eq("user_id", user?.id);
      }
      
      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;

      setClaims(data || []);
    } catch (error: any) {
      toast.error("Failed to load claims");
      console.error("Error fetching claims:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const handleStatusChange = async (claimId: string, newStatus: "pending" | "in_review" | "approved" | "denied") => {
    try {
      // Get current claim status before update
      const { data: currentClaim } = await supabase
        .from("claims")
        .select("status, claim_number")
        .eq("id", claimId)
        .single();

      const oldStatus = currentClaim?.status || "pending";
      const claimNumber = currentClaim?.claim_number;

      const { error } = await supabase
        .from("claims")
        .update({ status: newStatus })
        .eq("id", claimId);

      if (error) throw error;

      // Log the status change
      await AdminLogger.claimStatusChange(claimId, oldStatus, newStatus, claimNumber);

      // Send status change email notification
      try {
        await supabase.functions.invoke("send-status-change-email", {
          body: {
            claimId,
            oldStatus,
            newStatus,
          },
        });
      } catch (emailError) {
        console.error("Failed to send email notification:", emailError);
        // Don't fail the status update if email fails
      }

      toast.success("Claim status updated");
      fetchClaims();
    } catch (error: any) {
      toast.error("Failed to update claim status");
      console.error("Error updating claim status:", error);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "approved":
        return "default";
      case "denied":
        return "destructive";
      case "in_review":
        return "secondary";
      default:
        return "outline";
    }
  };

  const formatStatus = (status: string) => {
    return status.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-bold">Claims Dashboard</h1>
              {isAdmin && (
                <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                  Admin
                </span>
              )}
            </div>
            <p className="text-muted-foreground">
              {isAdmin ? "View and manage all claims" : "Manage and view your submitted claims"}
            </p>
          </div>
          <Button onClick={handleLogout} variant="outline">
            Logout
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{isAdmin ? "All Claims" : "Your Claims"}</CardTitle>
            <CardDescription>
              {claims.length} claim{claims.length !== 1 ? "s" : ""} {isAdmin ? "in system" : "submitted"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {claims.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No claims submitted yet</p>
                <Button onClick={() => navigate("/claims")}>I want to submit a new placement request</Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Claim #</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Policyholder</TableHead>
                      <TableHead>Adjuster</TableHead>
                      <TableHead>Loss Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Settlement</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {claims.map((claim) => (
                      <TableRow key={claim.id}>
                        <TableCell className="font-medium">
                          {claim.claim_number}
                        </TableCell>
                        <TableCell>{claim.claim_type}</TableCell>
                        <TableCell>{claim.policyholder_name}</TableCell>
                        <TableCell>{claim.adjuster_name}</TableCell>
                        <TableCell>
                          {format(new Date(claim.loss_date), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>
                          {isAdmin ? (
                            <Select
                              value={claim.status}
                              onValueChange={(value) => handleStatusChange(claim.id, value as "pending" | "in_review" | "approved" | "denied")}
                            >
                              <SelectTrigger className="w-[140px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_review">In Review</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="denied">Denied</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant={getStatusBadgeVariant(claim.status)}>
                              {formatStatus(claim.status)}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {claim.settlement_amount
                            ? `$${claim.settlement_amount.toLocaleString()}`
                            : "Pending"}
                        </TableCell>
                        <TableCell>
                          {format(new Date(claim.created_at), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/claim/${claim.id}`)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;